import type { Scenario } from '../types/lexce'

export const scenarios: Scenario[] = [
  {
    id: 'directions',
    label: 'Late for class',
    utterance:
      "Hey Lexce, I'm late for class and I don't know where to go. Where is my Intro to Philosophy class? Show me on Google Maps",
    summary: 'Reads the timetable, resolves the room, and opens a walking route.',
    turns: [
      {
        id: 'd-user',
        kind: 'user',
        text:
          "Hey Lexce, I'm late for class and I don't know where to go. Where is my Intro to Philosophy class? Show me on Google Maps",
      },
      {
        id: 'd-say',
        kind: 'say',
        text:
          'You have PHIL 101 — Intro to Philosophy from 10:30 to 11:45 with Dr. Nguyễn Thị Lan. It moved to Room B2.14 in the College of Arts & Sciences last week. From Dormitory A you are a 6 minute walk away, and class started 4 minutes ago.',
        highlights: ['Room B2.14', '6 min walk'],
      },
      {
        id: 'd-task',
        kind: 'task',
        task: {
          title: 'Getting you there',
          steps: [
            'Opening your timetable in My VinUni',
            'Matching Room B2.14 to the campus map',
            'Building a walking route from Dormitory A',
          ],
        },
      },
      {
        id: 'd-card',
        kind: 'card',
        card: {
          type: 'map',
          courseCode: 'PHIL 101',
          courseName: 'Intro to Philosophy',
          time: '10:30 – 11:45',
          instructor: 'Dr. Nguyễn Thị Lan',
          room: 'Room B2.14 · 2nd floor',
          building: 'College of Arts & Sciences (B2)',
          walkMinutes: 6,
          distance: '450 m',
          startsIn: 'Started 4 min ago',
          mapImage:
            'https://cdn.magicpatterns.com/patterns/generated-images/4e042122-1ce9-4ba4-8054-69daa12be33c.jpg',
          primaryAction: 'Open in Google Maps',
          secondaryAction: 'Tell Dr. Lan I am 6 min away',
        },
      },
      {
        id: 'd-chips',
        kind: 'chips',
        chips: ['Save B2.14 to my timetable', 'What did I miss today?', 'Where is my next class?'],
      },
    ],
  },
  {
    id: 'lostItem',
    label: 'Lost AirPods',
    utterance:
      'Hey Lexce, I lost my AirPods yesterday in the 2nd floor of the library. Help me file a report to CSD',
    summary: 'Checks Lost & Found, then files a CSD report with your details pre-filled.',
    turns: [
      {
        id: 'l-user',
        kind: 'user',
        text:
          'Hey Lexce, I lost my AirPods yesterday in the 2nd floor of the library. Help me file a report to CSD',
      },
      {
        id: 'l-say',
        kind: 'say',
        text:
          'Nothing matching AirPods has been checked into Library Lost & Found yet. The Center for Student Development keeps found items for 30 days, so filing a report now means you get notified the moment something matches. I can submit it for you — your last library tap-in was Thu 17 Sep, 19:42, Level 2.',
        highlights: ['No match yet', 'Last tap-in 19:42'],
      },
      {
        id: 'l-task',
        kind: 'task',
        task: {
          title: 'Filing the report with CSD',
          steps: [
            'Searching Lost & Found records for Level 2',
            'Pre-filling the CSD lost item form',
            'Attaching your library access log',
            'Submitting to CSD Student Services',
          ],
        },
      },
      {
        id: 'l-card',
        kind: 'card',
        card: {
          type: 'report',
          ticketId: 'CSD-2418',
          department: 'Center for Student Development',
          fields: [
            { label: 'Item', value: 'Apple AirPods Pro (2nd gen), white case', source: 'From your question' },
            { label: 'Last seen', value: 'Learning Commons — Level 2, study pod 14', source: 'Library access log' },
            { label: 'Date & time', value: 'Thu 17 Sep 2026, around 19:42', source: 'Card tap-in' },
            { label: 'Contact', value: 'Minh Anh Trần · V2028xxxx · +84 •• ••• 4412', source: 'Student profile' },
          ],
          timeline: [
            { label: 'Report submitted', detail: 'Just now', state: 'done' },
            { label: 'CSD review', detail: 'Expected within 1 working day', state: 'active' },
            { label: 'Match & pickup', detail: 'You will be notified in My VinUni', state: 'upcoming' },
          ],
          primaryAction: 'Track report',
          secondaryAction: 'Add a photo of the case',
        },
      },
      {
        id: 'l-chips',
        kind: 'chips',
        chips: ['Post to the campus Lost & Found board', 'Alert me if a match is found', 'CSD office hours'],
      },
    ],
  },
  {
    id: 'courses',
    label: 'Semester 2 advice',
    utterance:
      'Hey Lexce, what courses should I sign up for in my 2nd semester? Refer to the BBA curriculum framework and give me some advice',
    summary: 'Checks the BBA framework against your record and drafts a registration plan.',
    turns: [
      {
        id: 'c-user',
        kind: 'user',
        text:
          'Hey Lexce, what courses should I sign up for in my 2nd semester? Refer to the BBA curriculum framework and give me some advice',
      },
      {
        id: 'c-say',
        kind: 'say',
        text:
          'Under the BBA curriculum framework you need 15–18 credits in Semester 2, and two of them are gateways: ACCT 101 is a prerequisite for every Year 2 finance course, and MATH 121 unlocks the analytics track. You have already cleared ENGL 101, so my advice is to take 15 credits now — carrying an extra elective on top of two quantitative courses is where most first-years slip below the 3.0 they need for exchange eligibility.',
        highlights: ['15 credits', 'ACCT 101 is a gateway'],
      },
      {
        id: 'c-task',
        kind: 'task',
        task: {
          title: 'Drafting your registration plan',
          steps: [
            'Reading BBA Curriculum Framework 2026–27',
            'Comparing against your completed credits',
            'Checking prerequisites and timetable clashes',
            'Reserving 5 courses in your registration cart',
          ],
        },
      },
      {
        id: 'c-card',
        kind: 'card',
        card: {
          type: 'courses',
          term: 'Semester 2 · 2026–27',
          totalCredits: 15,
          courses: [
            {
              code: 'ACCT 101',
              name: 'Principles of Financial Accounting',
              credits: 3,
              slot: 'Mon / Wed 08:30',
              reason: 'Gateway to all Year 2 finance courses',
              requirement: 'Core',
            },
            {
              code: 'MATH 121',
              name: 'Business Statistics',
              credits: 3,
              slot: 'Tue / Thu 10:30',
              reason: 'Unlocks the business analytics track',
              requirement: 'Core',
            },
            {
              code: 'ECON 102',
              name: 'Principles of Macroeconomics',
              credits: 3,
              slot: 'Tue / Thu 13:00',
              reason: 'Follows ECON 101 from Semester 1',
              requirement: 'Core',
            },
            {
              code: 'MGMT 110',
              name: 'Organizational Behaviour',
              credits: 3,
              slot: 'Mon / Wed 13:00',
              reason: 'Lighter load to balance two quantitative courses',
              requirement: 'Foundation',
            },
            {
              code: 'VINU 130',
              name: 'Design Thinking & Innovation',
              credits: 3,
              slot: 'Thu 15:00',
              reason: 'Counts as your Semester 2 free elective',
              requirement: 'Elective',
            },
          ],
          caution: 'Fridays stay clear for your Vingroup mentorship sessions.',
          source: 'BBA Curriculum Framework 2026–27 · p.12, Year 1 progression',
          primaryAction: 'Add all 5 to registration cart',
          secondaryAction: 'Send plan to my academic advisor',
        },
      },
      {
        id: 'c-chips',
        kind: 'chips',
        chips: ['Swap MGMT 110 for a language course', 'Book time with my advisor', 'How does this affect my GPA?'],
      },
    ],
  },
]

export const scenarioById = (id: string) => scenarios.find((s) => s.id === id)
